/**
 * ChangeOrderPanel — Lists every change order for this WO and drives the
 * "New Change Order" and edit modals. Also renders the public share link
 * once finalized (Copy / SMS / Email / Preview / Rotate / Revoke).
 */
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Sparkle, FilePdf, Copy, EnvelopeSimple, PaperPlaneTilt, Eye,
  Trash, ArrowClockwise, CheckCircle, LockKey, LockKeyOpen, Warning,
} from "@phosphor-icons/react";
import ChangeOrderModal from "@/components/ChangeOrderModal";

const money = (c) => {
  const v = Math.abs(c || 0) / 100;
  return v % 1 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : `$${Math.round(v).toLocaleString()}`;
};

const STATUS_TONE = {
  draft:     { bg: "bg-slate-200 text-slate-700",   icon: LockKeyOpen, label: "Draft" },
  finalized: { bg: "bg-blue-100 text-blue-800",     icon: LockKey,     label: "Finalized" },
  sent:      { bg: "bg-amber-100 text-amber-800",   icon: PaperPlaneTilt, label: "Sent" },
  accepted:  { bg: "bg-emerald-100 text-emerald-800", icon: CheckCircle, label: "Accepted" },
};

export default function ChangeOrderPanel({ job }) {
  const jobId = job.id;
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [modalCO, setModal] = useState(null); // co object or "new" for new CO
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoad(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/change-orders`);
      setItems(data.items || []);
    } catch (_) { setItems([]); }
    finally { setLoad(false); }
  };
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  const remove = async (co) => {
    if (!window.confirm(`Delete Change Order #${co.co_number}?`)) return;
    setBusy(true);
    try { await api.delete(`/change-orders/${co.id}`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed")); }
    finally { setBusy(false); }
  };

  const finalize = async (co) => {
    setBusy(true);
    try {
      await api.post(`/change-orders/${co.id}/finalize`);
      toast.success("Finalized & link is live");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Finalize failed")); }
    finally { setBusy(false); }
  };

  const rotate = async (co) => {
    setBusy(true);
    try { await api.post(`/change-orders/${co.id}/share-link?rotate=true`); toast.success("Link rotated"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Rotate failed")); }
    finally { setBusy(false); }
  };

  const revoke = async (co) => {
    if (!window.confirm("Kill this public link?")) return;
    setBusy(true);
    try { await api.delete(`/change-orders/${co.id}/share-link`); toast.success("Revoked"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Revoke failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-md border-2 border-indigo-200 bg-indigo-50/50 p-3" data-testid="co-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FilePdf weight="fill" size={18} className="text-indigo-700"/>
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-widest text-indigo-800">Change Orders</div>
            <div className="text-[10px] text-slate-500">
              {items.length ? `${items.length} on this WO` : "None yet — mid-project scope changes go here"}
            </div>
          </div>
        </div>
        <button onClick={() => setModal("new")} disabled={busy}
          className="h-9 px-3 bg-gradient-to-r from-indigo-600 to-violet-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 shadow-[3px_3px_0_rgba(0,0,0,0.35)]"
          data-testid="co-new-btn">
          <Plus weight="bold" size={12}/> New Change Order
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400 mt-2">Loading…</div>
      ) : items.length ? (
        <div className="mt-3 space-y-2">
          {items.map(co => <CORow key={co.id} co={co} onEdit={() => setModal(co)}
                                    onDelete={() => remove(co)}
                                    onFinalize={() => finalize(co)}
                                    onRotate={() => rotate(co)}
                                    onRevoke={() => revoke(co)}/>)}
        </div>
      ) : null}

      {modalCO && (
        <ChangeOrderModal
          jobId={jobId}
          existing={modalCO === "new" ? null : modalCO}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CORow({ co, onEdit, onDelete, onFinalize, onRotate, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const tone = STATUS_TONE[co.status] || STATUS_TONE.draft;
  const StatusIcon = tone.icon;
  const url = co.share_token
    ? `${window.location.origin}/api/public/change-order/${co.share_token}/pdf`
    : "";
  const smsText = `Change Order #${co.co_number} — ${co.parent_property_address}${co.parent_unit ? ` #${co.parent_unit}` : ""}\n${money(co.total_cents || 0)}\n${url}`;
  const emailSubj = `Change Order #${co.co_number} — ${co.parent_property_address}`;
  const emailBody = `Change Order #${co.co_number} for your review and approval:\n${url}\n\nSent from Field Radar`;

  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch { toast.error("Copy failed — long-press to copy"); }
  };

  const previewPdf = async () => {
    const token = localStorage.getItem("access_token");
    const u = `${API}/change-orders/${co.id}/pdf`;
    try {
      const res = await fetch(u, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div className="rounded-md bg-white border-2 border-slate-200 overflow-hidden">
      <div className="p-2 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${tone.bg}`}>
          <StatusIcon weight="fill" size={10}/> {tone.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-black text-slate-900">
            Change Order #{co.co_number}
            <span className="ml-2 text-[10px] font-normal text-slate-500 truncate">{co.section_title}</span>
          </div>
          <div className="text-[10px] text-slate-500">
            {money(co.total_cents || 0)}
            {co.discount_cents > 0 ? ` · ${co.discount_label || "discount"} ${money(co.discount_cents)} off` : ""}
            {co.share_view_count > 0 ? ` · ${co.share_view_count} view${co.share_view_count === 1 ? "" : "s"}` : ""}
            {co.accepted_at ? ` · accepted by ${co.accepted_by_name}` : ""}
          </div>
        </div>
        <button onClick={previewPdf} className="h-8 px-2 border border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid={`co-preview-${co.id}`}>
          <Eye weight="bold" size={10}/> Preview
        </button>
        {co.status === "draft" && (
          <>
            <button onClick={onEdit} className="h-8 px-2 border border-indigo-300 text-[10px] font-black uppercase tracking-widest text-indigo-700 hover:bg-indigo-100 inline-flex items-center gap-1"
              data-testid={`co-edit-${co.id}`}>
              Edit
            </button>
            <button onClick={onFinalize} className="h-8 px-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1"
              data-testid={`co-finalize-${co.id}`}>
              <LockKey weight="fill" size={10}/> Finalize
            </button>
            <button onClick={onDelete} className="h-8 w-8 text-slate-400 hover:text-red-500 flex items-center justify-center"
              data-testid={`co-delete-${co.id}`}>
              <Trash weight="bold" size={12}/>
            </button>
          </>
        )}
      </div>
      {co.share_token && !co.share_revoked && (
        <div className="border-t border-slate-100 bg-indigo-50/50 p-2 flex items-center gap-1 flex-wrap">
          <button onClick={copy}
            className="h-7 px-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`co-copy-${co.id}`}>
            <Copy weight="bold" size={10}/> {copied ? "Copied" : "Copy Link"}
          </button>
          <a href={`sms:?&body=${encodeURIComponent(smsText)}`}
            className="h-7 px-2 bg-white border border-indigo-300 text-indigo-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`co-sms-${co.id}`}>
            <PaperPlaneTilt weight="fill" size={10}/> SMS
          </a>
          <a href={`mailto:?subject=${encodeURIComponent(emailSubj)}&body=${encodeURIComponent(emailBody)}`}
            className="h-7 px-2 bg-white border border-indigo-300 text-indigo-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1"
            data-testid={`co-email-${co.id}`}>
            <EnvelopeSimple weight="bold" size={10}/> Email
          </a>
          <button onClick={() => window.open(url, "_blank")}
            className="h-7 px-2 bg-white border border-indigo-300 text-indigo-700 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
            <Eye weight="bold" size={10}/> Open Public
          </button>
          <div className="flex-1"/>
          <button onClick={onRotate} title="Rotate token — old link stops working"
            className="h-7 w-7 text-slate-500 hover:text-slate-900 flex items-center justify-center">
            <ArrowClockwise weight="bold" size={12}/>
          </button>
          <button onClick={onRevoke} title="Kill this link"
            className="h-7 w-7 text-slate-500 hover:text-red-600 flex items-center justify-center">
            <Trash weight="bold" size={12}/>
          </button>
        </div>
      )}
      {co.share_revoked && (
        <div className="border-t border-red-100 bg-red-50 p-1.5 flex items-center gap-1 text-[10px] text-red-800">
          <Warning weight="fill" size={11}/> Public link revoked — mint a new one from the row menu.
        </div>
      )}
    </div>
  );
}
